import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Link,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { isValidEmail } from "@utils/validators";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import {
  BillingInvoiceDto,
  EmailInvoicesDto,
  IEmailInvoicesDto,
} from "@lib/ShiOneClient";
import { sendOrderInvoicesToEmail } from "@api/invoiceStore";
import { Toast } from "shared-ui/src/components/Toast";
import { useGetOrderInvoices } from "@features/orders-features/hooks/useGetOrderInvoices";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { DiscardChangesPrompt } from "shared-ui/src/hooks/routedOverlays/DirtyDialogBlocker";

const PRIVACY_STATEMENT_URL = "https://www.shi.com/terms/privacy-statement";

const splitEmails = (value: string): string[] =>
  value
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);

const dedupeEmails = (emails: string[]): string[] => {
  const seen = new Set<string>();
  return emails.filter((email) => {
    const normalized = email.toLowerCase();
    if (seen.has(normalized)) {
      return false;
    }
    seen.add(normalized);
    return true;
  });
};

type EmailOrderInvoicesProps = {
  open: boolean;
  onClose: () => void;
  salesOrder?: string;
  biOrderId?: number;
  custPONumber?: string;
  onDirtyChange?: (dirty: boolean) => void;
};

export default function EmailOrderInvoicesDialog({
  open,
  onClose,
  salesOrder,
  biOrderId,
  custPONumber,
  onDirtyChange,
}: EmailOrderInvoicesProps) {
  const { getClaimValue } = useClaims();
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState<string>("");
  const [emailError, setEmailError] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [discardPromptOpen, setDiscardPromptOpen] = useState(false);
  const {
    data: orderInvoices,
    isLoading,
    isError,
  } = useGetOrderInvoices({
    biOrderId,
    orderNumber: salesOrder,
    custPONumber,
  });

  const defaultEmails = useMemo(() => {
    const emailDefaults = [];
    const userEmail = getClaimValue("userEmail") ?? undefined;
    if (userEmail && isValidEmail(userEmail)) {
      emailDefaults.push(userEmail);
    }
    const impersonatorEmail = getClaimValue("impersonatorEmail") ?? undefined;
    if (impersonatorEmail && isValidEmail(impersonatorEmail)) {
      emailDefaults.push(impersonatorEmail);
    }
    return emailDefaults;
  }, [getClaimValue]);

  useEffect(() => {
    if (open) {
      setEmails(defaultEmails);
      setEmailInput("");
      setEmailError("");
      setSelectedInvoices([]);
      setIsSending(false);
    }
  }, [open, defaultEmails]);

  const isDirty =
    selectedInvoices.length > 0 ||
    emailInput.trim().length > 0 ||
    emails.length !== defaultEmails.length ||
    emails.some((email, index) => email !== defaultEmails[index]);

  useEffect(() => {
    onDirtyChange?.(open && isDirty);
  }, [open, isDirty, onDirtyChange]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setDiscardPromptOpen(true);
      return;
    }
    onClose();
  }, [isDirty, onClose]);

  const handleDiscard = useCallback(() => {
    setDiscardPromptOpen(false);
    onClose();
  }, [onClose]);

  const addEmails = (value: string) => {
    const candidates = splitEmails(value);
    if (candidates.length === 0) {
      setEmailError("");
      return "";
    }

    const valid: string[] = [];
    const invalid: string[] = [];

    candidates.forEach((candidate) => {
      if (isValidEmail(candidate)) {
        valid.push(candidate);
      } else {
        invalid.push(candidate);
      }
    });

    if (valid.length > 0) {
      setEmails((prev) => dedupeEmails([...prev, ...valid]));
    }

    if (invalid.length > 0) {
      setEmailError(
        `Invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`,
      );
    } else {
      setEmailError("");
    }
    return invalid.join(", ");
  };

  const commitEmails = () => {
    setEmailInput(addEmails(emailInput));
  };

  const handleInvoiceToggle = (invoiceId: string) => {
    setSelectedInvoices((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId],
    );
  };

  const handleSend = useCallback(async () => {
    if (!orderInvoices?.length) {
      return;
    }

    const [primaryEmail, ...additionalEmailAddresses] = emails;
    if (!primaryEmail) {
      return;
    }

    const invoicesToSend = orderInvoices
      .filter(
        (p) => p.invoiceNumber && selectedInvoices.includes(p.invoiceNumber),
      )
      .map(
        (p) =>
          new BillingInvoiceDto({
            invoiceId: p.invoiceNumber,
            invoiceDate: p.invoicedOn,
            tag: "none",
          }),
      );

    if (invoicesToSend.length === 0) {
      return;
    }

    setIsSending(true);

    try {
      const emailRequest: IEmailInvoicesDto = {
        emailAddress: primaryEmail,
        invoices: invoicesToSend,
        isInvoicedOnRange: true,
        additionalEmailAddresses,
      };
      const res = await sendOrderInvoicesToEmail(emailRequest as EmailInvoicesDto);
      const errorDetails =
        res.invoiceResponses?.flatMap(({ statusCode, message, invoiceId }) => {
          const msg = message?.trim();
          const id = invoiceId?.trim();

          if (statusCode === 200) {
            return [];
          }

          const normalizedMsg = msg ?? (id ? `${id}: N/A` : "");

          if (!normalizedMsg) {
            return [];
          }

          if (!id || normalizedMsg.includes(id)) {
            return [normalizedMsg];
          }

          return [`${id}: ${normalizedMsg}`];
        }) ?? [];

      if (res.allSuccessful) {
        const nInvoice =
          invoicesToSend.length > 1 ? "invoices have been" : "invoice has been";
        const nAddress = emails.length > 1 ? "addresses" : "address";
        Toast(
          `The requested ${nInvoice} sent to the selected email ${nAddress}. If you have any questions, please contact your Account Executive.`,
          false,
        );
      } else if (errorDetails.length > 0) {
        Toast(
          <Box>
            <Typography sx={{ mb: 1 }}>
              The following invoices were unable to be emailed:
            </Typography>
            <Box component="ul" sx={{ my: 0, pl: 3 }}>
              {errorDetails.map((message, index) => (
                <li key={`${message}-${index}`}>
                  <Typography component="span">{message}</Typography>
                </li>
              ))}
            </Box>
            <Typography sx={{ mt: 1 }}>
              Please contact your Account Executive for assistance.
            </Typography>
          </Box>,
          true,
        );
      } else {
        Toast(
          "There was an error emailing one or more invoices. Please contact your Account Executive for assistance.",
          true,
        );
      }
    } catch {
      Toast(
        "There was an error emailing one or more invoices. Please contact your Account Executive for assistance.",
        true,
      );
    } finally {
      setIsSending(false);
      onClose();
    }
  }, [orderInvoices, selectedInvoices, emails, onClose]);

  const invoiceOptions = useMemo(
    () => orderInvoices?.filter((p) => !!p.invoiceNumber) ?? [],
    [orderInvoices],
  );
  const selectedInvoicePayloadCount = invoiceOptions.filter((p) =>
    selectedInvoices.includes(p.invoiceNumber!),
  ).length;
  const invoiceGridTemplateColumns =
    invoiceOptions.length > 3 ? "1fr 1fr 1fr" : "1fr";

  const sendDisabled =
    isSending ||
    isError ||
    invoiceOptions.length === 0 ||
    selectedInvoicePayloadCount === 0 ||
    emails.length === 0 ||
    Boolean(emailError);

  const orderReference = [salesOrder, custPONumber && `PO# ${custPONumber}`]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderBottomColor: "grey.300",
            mb: 1,
          }}
        >
          <Typography variant="h6" component="span">
            Email Invoices
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            {...telemetryProps({
              name: "orders_email_dialog_close",
              section: "orders_email_dialog",
              label: "Close email invoices dialog",
              ...(salesOrder ? { id: salesOrder } : {}),
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Select the invoices and enter the email address to receive the
            invoices.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            <Box component="span" sx={{ mr: 1 }}>
              Order:
            </Box>
            {orderReference}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Invoices
          </Typography>
          {isLoading ? (
            <Skeleton
              role="status"
              aria-label="Loading invoices"
              sx={{ mb: 2.25 }}
            />
          ) : isError ? (
            <Alert severity="error" sx={{ mb: 2.25 }}>
              <Typography variant="body2">
                Invoices cannot be retrieved at this time. Refresh this page or
                try again later. If the problem persists, contact your Account
                Team.
              </Typography>
            </Alert>
          ) : invoiceOptions.length === 0 ? (
            <Typography variant="body1" sx={{ mb: 2.25 }}>
              No invoices found.
            </Typography>
          ) : (
            <FormGroup
              sx={{
                mb: 2.25,
                display: "grid",
                gridTemplateColumns: invoiceGridTemplateColumns,
                columnGap: 2.25,
              }}
            >
              {invoiceOptions.map((p) => (
                <FormControlLabel
                  key={p.invoiceNumber}
                  control={
                    <Checkbox
                      checked={selectedInvoices.includes(p.invoiceNumber!)}
                      onChange={() => handleInvoiceToggle(p.invoiceNumber!)}
                    />
                  }
                  label={p.invoiceNumber}
                />
              ))}
            </FormGroup>
          )}
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={emails}
            inputValue={emailInput}
            onInputChange={(_event, value) => setEmailInput(value)}
            onChange={(_event, value) => {
              const nextEmails = dedupeEmails(
                value
                  .flatMap((entry) => splitEmails(entry))
                  .filter((entry) => isValidEmail(entry)),
              );
              setEmails(nextEmails);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Email To"
                placeholder="Add email"
                error={Boolean(emailError)}
                helperText={
                  emailError ||
                  "Separate email addresses with Enter, comma, or semicolon."
                }
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" ||
                    event.key === "," ||
                    event.key === ";"
                  ) {
                    event.preventDefault();
                    commitEmails();
                  }
                }}
                onBlur={commitEmails}
              />
            )}
            sx={{ mb: 2 }}
          />
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              borderRadius: 1,
            }}
          >
            <Alert severity="info">
              <Typography variant="body2">
                By filling out this form, I expressly consent to the collection
                and processing by SHI of the personal data I submit for the
                purpose of this request. Your rights and our commitments regarding
                your personal data are detailed in our{" "}
                <Link
                  href={PRIVACY_STATEMENT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Statement.
                </Link>
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            borderTop: "1px solid",
            borderTopColor: "grey.300",
          }}
        >
          <Button
            color="inherit"
            onClick={handleClose}
            {...telemetryProps({
              name: "orders_email_dialog_cancel",
              section: "orders_email_dialog",
              label: "Cancel",
              ...(salesOrder ? { id: salesOrder } : {}),
            })}
          >
            CANCEL
          </Button>
          <Box sx={{ m: 1, position: "relative" }}>
            <Button
              color="primary"
              variant="text"
              onClick={handleSend}
              disabled={sendDisabled}
              loading={isSending}
              {...telemetryProps({
                name: "orders_email_dialog_send",
                section: "orders_email_dialog",
                label: "Send invoices",
                ...(salesOrder ? { id: salesOrder } : {}),
              })}
            >
              SEND
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <DiscardChangesPrompt
        open={discardPromptOpen}
        onKeepEditing={() => setDiscardPromptOpen(false)}
        onDiscard={handleDiscard}
      />
    </>
  );
}
